import React, { useContext, useState } from "react";
import { useModal } from "react-modal-hook";
import { useDeleteSavedViewMutation } from "../../../../../generated/graphql";
import { useStatusToasts } from "../../../../../hooks/useStatusToasts";
import { spacing } from "../../../../../stitches.config";
import Checkbox from "../../../../Inputs/Checkbox";
import { FlexColumn } from "../../../__styles__/Layout";
import { CommonModal } from "../../../CommonModal";
import Modal from "../../../Modal";
import { Body } from "../../../Typography";
import { TableContext } from "../../TableContext";
import { useUpdateSavedView } from "../../utils";
import { SavedView } from "../utils";

const DeleteSavedViewModal = ({
  savedView,
  hideModal,
  currentView,
  defaultView,
}: {
  savedView: SavedView;
  hideModal: () => void;
  currentView: SavedView;
  defaultView: SavedView;
}) => {
  const { updateSavedView } = useUpdateSavedView();

  const { addErrorToast, addSuccessToast } = useStatusToasts();

  const [confirmDashboardRemoval, setConfirmDashboardRemoval] = useState(false);
  const [confirmPublicWebsiteRemoval, setConfirmPublicWebsiteRemoval] =
    useState(false);
  const [confirmHighlightsRemoval, setConfirmHighlightsRemoval] =
    useState(false);

  const allCheckboxesChecked =
    confirmDashboardRemoval &&
    confirmPublicWebsiteRemoval &&
    confirmHighlightsRemoval;

  const [deleteSavedView, { loading }] = useDeleteSavedViewMutation({
    onCompleted: () => {
      const deletedCurrentView = savedView.id === currentView.id;

      if (deletedCurrentView) {
        updateSavedView(defaultView.id);
      }

      hideModal();

      addSuccessToast(`Your table view was successfully deleted`);
    },
    onError: error => {
      const errorMessage = error.graphQLErrors
        .map(error => error.message)
        .join("\n");
      addErrorToast(errorMessage);
    },
  });

  return (
    <CommonModal
      title="Delete saved view"
      subtitle={savedView.name}
      primaryButtonText="Delete"
      variant="destructive"
      disabled={!allCheckboxesChecked}
      onSubmit={() => {
        void deleteSavedView({
          variables: { id: savedView.id },
          refetchQueries: ["GetSavedViews"],
        });
      }}
      loading={loading}
      onCancel={hideModal}
      size="medium"
      content={
        <FlexColumn style={{ gap: spacing.m.value }}>
          <Body size="default" type="regular">
            By deleting this saved view, you confirm that:
          </Body>
          <FlexColumn style={{ gap: spacing.s.value }}>
            <Checkbox
              value={confirmDashboardRemoval}
              onChange={setConfirmDashboardRemoval}
              label="The saved view will be removed from the dashboard for all users."
              required
            />
            <Checkbox
              value={confirmPublicWebsiteRemoval}
              onChange={setConfirmPublicWebsiteRemoval}
              label="The saved view will no longer be accessible from your public website."
              required
            />
            <Checkbox
              value={confirmHighlightsRemoval}
              onChange={setConfirmHighlightsRemoval}
              label="Any highlights created on your public website homepage will be removed."
              required
            />
          </FlexColumn>
          <Body size="default" type="regular">
            This action <span style={{ fontWeight: 500 }}>cannot</span> be
            undone.
          </Body>
        </FlexColumn>
      }
    />
  );
};

export const useDeleteSavedViewModal = () => {
  const { defaultView, currentView } = useContext(TableContext);
  const [savedView, setSavedView] = useState<SavedView | null>(null);

  let [showModal, hideModal] = useModal(
    () => (
      <Modal onRequestClose={hideModal}>
        <DeleteSavedViewModal
          savedView={savedView!}
          hideModal={() => {
            hideModal();
            setSavedView(null);
          }}
          defaultView={defaultView!}
          currentView={currentView!}
        />
      </Modal>
    ),
    [defaultView, savedView, currentView],
  );

  const showDeleteSavedViewModal = (savedView: SavedView) => {
    setSavedView(savedView);
    showModal();
  };

  return showDeleteSavedViewModal;
};
